import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { THERAPISTS } from "@/data/therapists";
import TherapistCard from "@/components/TherapistCard";
import { Reveal } from "@/components/Reveal";
import BotanicalArt from "@/components/BotanicalArt";

const CONCERNS = [
  { id: "anxiety", label: "Anxiety & overthinking", hint: "Racing thoughts, worry loops, restlessness" },
  { id: "stress", label: "Stress & burnout", hint: "Work, studies, or just running on empty" },
  { id: "relationships", label: "Relationships", hint: "Partners, family, friendships, patterns" },
  { id: "self-esteem", label: "Self-worth & identity", hint: "Self-doubt, image, not feeling enough" },
  { id: "trauma", label: "Grief & difficult experiences", hint: "Loss, or something from the past" },
  { id: "unsure", label: "I'm not sure yet", hint: "That's completely fine — we'll figure it out" },
];

const SPOKEN_LANGUAGES = [...new Set(THERAPISTS.flatMap((t) => t.languages))].sort();
const LANGUAGES = ["No preference", ...SPOKEN_LANGUAGES];

const stepAnim = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

export default function FindTherapist() {
  const [step, setStep] = useState(0);
  const [concern, setConcern] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const c = searchParams.get("concern");
    if (c && CONCERNS.some((o) => o.id === c)) {
      setConcern(c);
      setStep(1);
    }
  }, [searchParams]);
  const [language, setLanguage] = useState(null);

  const results = useMemo(() => {
    const scored = THERAPISTS.map((t) => {
      let score = 0;
      if (concern && concern !== "unsure" && t.concerns.includes(concern)) score += 2;
      if (language && language !== "No preference" && t.languages.includes(language)) score += 1;
      return { t, score };
    });
    return scored.sort((a, b) => b.score - a.score);
  }, [concern, language]);

  return (
    <div className="mx-auto max-w-screen-xl px-6 py-16 md:px-12 md:py-24" data-testid="find-page">
      <div className="grid items-center gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-accent">
            <span className="h-px w-10 bg-accent" /> Therapist matching
          </p>
          <h1 className="mt-6 max-w-3xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-6xl">
            Let's find someone who feels right for you.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Two gentle questions. No sign-up, no commitment — just a quieter way to see
            who you might feel comfortable talking to.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="hidden lg:col-span-5 lg:block">
          <BotanicalArt variant="knot" caption="Finding the thread that fits." />
        </Reveal>
      </div>

      <div className="mt-14">
        {step < 2 && (
          <div className="mb-10 flex items-center gap-3" data-testid="match-progress">
            {[0, 1].map((i) => (
              <div key={i} className={`h-1 rounded-full transition-all duration-500 ${step >= i ? "w-16 bg-accent" : "w-8 bg-border"}`} />
            ))}
            <span className="ml-2 text-xs font-semibold text-muted-foreground">Step {step + 1} of 2</span>
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="q1" {...stepAnim}>
              <h2 className="font-serif text-2xl font-bold tracking-tight md:text-3xl" data-testid="match-q1">
                What would you like support with?
              </h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {CONCERNS.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`concern-${c.id}`}
                    onClick={() => { setConcern(c.id); setStep(1); }}
                    className={`rounded-2xl border p-6 text-left transition-all duration-300 hover:-translate-y-1 hover:border-accent ${
                      concern === c.id ? "border-accent bg-card" : "border-primary/10 bg-card"
                    }`}
                  >
                    <p className="font-serif text-xl font-bold tracking-tight">{c.label}</p>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{c.hint}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => { setConcern("unsure"); setStep(2); }}
                data-testid="match-browse-all"
                className="mt-8 text-sm font-semibold text-muted-foreground underline-offset-4 transition-colors hover:text-accent hover:underline"
              >
                Not sure? Browse all therapists instead
              </button>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="q2" {...stepAnim}>
              <h2 className="font-serif text-2xl font-bold tracking-tight md:text-3xl" data-testid="match-q2">
                Which language would you be most comfortable in?
              </h2>
              <div className="mt-8 flex flex-wrap gap-3">
                {LANGUAGES.map((l) => (
                  <button
                    key={l}
                    data-testid={`language-${l.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => { setLanguage(l); setStep(2); }}
                    className="rounded-full border border-primary/15 bg-card px-6 py-3 text-sm font-semibold transition-all duration-300 hover:scale-105 hover:border-accent hover:bg-primary hover:text-primary-foreground"
                  >
                    {l}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(0)}
                data-testid="match-back-q1"
                className="mt-10 flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-accent"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="results" {...stepAnim} data-testid="match-results">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="font-serif text-2xl font-bold tracking-tight md:text-3xl">
                  {concern && concern !== "unsure"
                    ? "Therapists who fit what you told us."
                    : "Every Eunoia therapist."}
                </h2>
                <button
                  onClick={() => { setStep(0); setConcern(null); setLanguage(null); }}
                  data-testid="match-restart"
                  className="text-sm font-semibold text-muted-foreground underline-offset-4 transition-colors hover:text-accent hover:underline"
                >
                  Start over
                </button>
              </div>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
                Take your time. Read their profiles, notice who you feel drawn to — that instinct
                matters more than any resume.
              </p>
              <div
                className="mt-10 grid justify-center gap-6"
                style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 260px))" }}
              >
                {results.map(({ t }, i) => (
                  <TherapistCard key={t.id} therapist={t} index={i} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
